import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Search, Plus, X, Trash2, Settings, Save, Zap, ScanLine, Lock, ArrowRightLeft } from "lucide-react";
import { FOOD_LIBRARY, CATEGORIES } from "@/data/foodLibrary";
import BarcodeScanner from "@/components/BarcodeScanner";
import FoodPhotoButton from "@/components/FoodPhotoButton";
import PhoenixCauldron from "@/components/PhoenixCauldron";
import { NutrientChart } from "@/components/NutrientChart";
import PremiumGate, { usePremium, PremiumBadge } from "@/components/PremiumGate";
import LogCalendar from "@/components/LogCalendar";
import useUserSync from "@/hooks/useUserSync";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOG_KEY = "omnimuscle_food_log_v1";
const CUSTOM_KEY = "omnimuscle_custom_foods_v1";
const TARGETS_KEY = "omnimuscle_food_targets_v1";
const TDEE_KEY = "omnimuscle_tdee_v1";

const MEALS = ["Breakfast", "Lunch", "Dinner", "Snack"];

function todayKey() {
  // PT date (America/Los_Angeles). All daily resets in OmniMuscle flip at
  // 00:00 PT, NOT 00:00 UTC. Critical for Fuel-The-Work totals so calories
  // don't reset at 5pm PT (which is UTC midnight).
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  const d = parts.find((p) => p.type === "day")?.value;
  return `${y}-${m}-${d}`;
}

function fmt(n) {
  if (n == null || isNaN(n)) return "—";
  return Math.round(n);
}
function fmt1(n) {
  if (n == null || isNaN(n)) return "—";
  return Number(n).toFixed(1);
}

export default function FoodLog() {
  const { logActivity } = useAuth();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [picked, setPicked] = useState(null); // food being added
  const [servings, setServings] = useState("1");
  const [meal, setMeal] = useState("Breakfast");
  const [log, setLog] = useState([]);
  const [customFoods, setCustomFoods] = useState([]);
  const [showCustom, setShowCustom] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [showPremiumGate, setShowPremiumGate] = useState(false);
  const [showTargets, setShowTargets] = useState(false);
  const isPremium = usePremium();
  const [manualTargets, setManualTargets] = useState(null);
  const [tdeeData, setTdeeData] = useState(null);
  // 🔄 Today's Extras (Quick Log) — fetched from backend so Fuel The Work totals
  // include calories logged via the homepage Daily Vitals card.
  const [quickEntries, setQuickEntries] = useState([]);
  // 📅 Shared calendar — selected date + panel toggle. Default = today (PT).
  // Lets the user log for missed days (retroactive) or plan future meals.
  const [selectedDate, setSelectedDate] = useState(todayKey());
  const [showCalendar, setShowCalendar] = useState(false);

  /* Load persisted state */
  useEffect(() => {
    try {
      const l = localStorage.getItem(LOG_KEY);
      if (l) setLog(JSON.parse(l));
      const c = localStorage.getItem(CUSTOM_KEY);
      if (c) setCustomFoods(JSON.parse(c));
      const t = localStorage.getItem(TARGETS_KEY);
      if (t) setManualTargets(JSON.parse(t));
      const tdee = localStorage.getItem(TDEE_KEY);
      if (tdee) setTdeeData(JSON.parse(tdee));
    } catch {
      // ignore
    }
  }, []);

  /* Refresh tdee on focus (in case Tools updates while open) */
  useEffect(() => {
    const onFocus = () => {
      try {
        const tdee = localStorage.getItem(TDEE_KEY);
        if (tdee) setTdeeData(JSON.parse(tdee));
      } catch {
        // ignore
      }
      loadQuickLog();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  /* Pull Today's Extras (quick_log) from backend so their calories flow into
     the Fuel The Work totals. Re-polls every 30s in case the user logs more
     from the homepage in another tab. */
  async function loadQuickLog() {
    try {
      const { data } = await axios.get(`${API}/vitals/today`);
      // /api/vitals/today returns a flat `quick` array already filtered to PT today.
      const items = Array.isArray(data?.quick) ? data.quick : [];
      setQuickEntries(items);
    } catch {
      // silent — guest or offline
      setQuickEntries([]);
    }
  }
  useEffect(() => {
    loadQuickLog();
    const id = setInterval(loadQuickLog, 30000);
    return () => clearInterval(id);
  }, []);

  /* Persist */
  useEffect(() => {
    try {
      localStorage.setItem(LOG_KEY, JSON.stringify(log));
    } catch {
      // ignore
    }
  }, [log]);
  useEffect(() => {
    try {
      localStorage.setItem(CUSTOM_KEY, JSON.stringify(customFoods));
    } catch {
      // ignore
    }
  }, [customFoods]);
  useEffect(() => {
    try {
      if (manualTargets) localStorage.setItem(TARGETS_KEY, JSON.stringify(manualTargets));
      else localStorage.removeItem(TARGETS_KEY);
    } catch {
      // ignore
    }
  }, [manualTargets]);

  // Cross-device sync (no-op when logged out)
  useUserSync({
    scope: "food",
    readLocal: () => ({
      log,
      custom_foods: customFoods,
      targets: manualTargets,
      tdee: tdeeData,
    }),
    writeLocal: (merged) => {
      if (Array.isArray(merged?.log)) setLog(merged.log);
      if (Array.isArray(merged?.custom_foods)) setCustomFoods(merged.custom_foods);
      if (merged?.targets) setManualTargets(merged.targets);
      if (merged?.tdee) {
        setTdeeData(merged.tdee);
        try { localStorage.setItem(TDEE_KEY, JSON.stringify(merged.tdee)); } catch {}
      }
    },
    merge: (server, local) => {
      const dedupe = (a, b, keyFn) => {
        const seen = new Set();
        const out = [];
        for (const e of [...(a || []), ...(b || [])]) {
          if (!e) continue;
          const k = keyFn(e);
          if (seen.has(k)) continue;
          seen.add(k);
          out.push(e);
        }
        return out;
      };
      return {
        log: dedupe(
          server?.log,
          local?.log,
          (e) => e.id || `${e.date}|${e.meal}|${e.name}|${e.servings}|${e.calories}`
        ),
        custom_foods: dedupe(
          server?.custom_foods,
          local?.custom_foods,
          (e) => e.id || e.name
        ),
        targets: local?.targets ?? server?.targets,
        tdee: local?.tdee ?? server?.tdee,
      };
    },
    deps: [log, customFoods, manualTargets, tdeeData],
  });

  /* Compute targets — manual override if set, else from TDEE */
  const targets = useMemo(() => {
    if (manualTargets) return manualTargets;
    if (tdeeData?.target) {
      // Default macro split: 30% P / 45% C / 25% F
      const cal = tdeeData.target;
      return {
        cal,
        p: Math.round((cal * 0.3) / 4),
        c: Math.round((cal * 0.45) / 4),
        f: Math.round((cal * 0.25) / 9),
      };
    }
    return null;
  }, [manualTargets, tdeeData]);

  /* All foods (library + custom) */
  const allFoods = useMemo(() => [...FOOD_LIBRARY, ...customFoods], [customFoods]);

  /* Filtered list */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allFoods.filter((food) => {
      if (category !== "All" && food.category !== category) return false;
      if (q && !food.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [search, category, allFoods]);

  /* Today's log + totals — driven by the shared calendar's selectedDate.
     Lets the user retroactively log missed days OR plan future meals. */
  const today = todayKey();
  const todayLog = log.filter((e) => e.date === selectedDate);
  const isViewingToday = selectedDate === today;
  // 📅 Cross-stream dots for the calendar — reads FoodLog + MuscleTracker
  // localStorage entries and maps each date → ["food","gym","cardio"].
  const dotsByDate = useMemo(() => {
    const dots = {};
    (log || []).forEach((e) => {
      if (!e.date) return;
      const arr = dots[e.date] || [];
      if (!arr.includes("food")) arr.push("food");
      dots[e.date] = arr;
    });
    try {
      const gymRaw = localStorage.getItem("omnimuscle_log_v1");
      if (gymRaw) {
        const gymLog = JSON.parse(gymRaw);
        gymLog.forEach((e) => {
          if (!e.date) return;
          const arr = dots[e.date] || [];
          const tag = e.type === "cardio" ? "cardio" : "gym";
          if (!arr.includes(tag)) arr.push(tag);
          dots[e.date] = arr;
        });
      }
    } catch (_e) { /* ignore */ }
    return dots;
  }, [log, selectedDate]);
  // Sum EVERY macro column from Quick Log (Today's Extras) into the totals
  // so the Fuel-The-Work tracker + macro rows reflect every meal — whether
  // logged via full Food Log, Snap-a-Meal (AI Vision), or the manual Quick
  // Log modal with optional P/C/F. As of 2026-03-01 Quick Log entries carry
  // full macros (protein_g / carbs_g / fat_g / fiber_g / sugar_g / sodium_mg)
  // whenever the source provides them; missing fields fall through as 0
  // so calorie-only entries (rare, manual-typed) still contribute kcal.
  const quickTotalsToday = quickEntries.reduce(
    (acc, q) => ({
      cal: acc.cal + (Number(q.calories)  || 0),
      p:   acc.p   + (Number(q.protein_g) || 0),
      c:   acc.c   + (Number(q.carbs_g)   || 0),
      f:   acc.f   + (Number(q.fat_g)     || 0),
      fib: acc.fib + (Number(q.fiber_g)   || 0),
      sug: acc.sug + (Number(q.sugar_g)   || 0),
      na:  acc.na  + (Number(q.sodium_mg) || 0),
    }),
    { cal: 0, p: 0, c: 0, f: 0, fib: 0, sug: 0, na: 0 }
  );
  const quickCalToday = quickTotalsToday.cal;
  const totals = todayLog.reduce(
    (a, e) => ({
      cal: a.cal + e.cal,
      p: a.p + e.p,
      c: a.c + e.c,
      f: a.f + e.f,
      fib: a.fib + (e.fib || 0),
      sug: a.sug + (e.sug || 0),
      na: a.na + (e.na || 0),
    }),
    { ...quickTotalsToday }
  );

  const addFood = async () => {
    if (!picked) return;
    const s = parseFloat(servings) || 1;
    const entry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      date: selectedDate,   // 📅 use calendar-selected date (retroactive/future OK)
      meal,
      foodId: picked.id,
      name: picked.name,
      serving: picked.serving,
      servings: s,
      cal: picked.cal * s,
      p: picked.p * s,
      c: picked.c * s,
      f: picked.f * s,
      fib: (picked.fib || 0) * s,
      sug: (picked.sug || 0) * s,
      na: (picked.na || 0) * s,
    };
    setLog((l) => [...l, entry]);
    setPicked(null);
    setServings("1");
    logActivity?.("food");
    // 📊 Persist to the UNIFIED daily_log so AI Coach + Kitchen + Gym +
    // Tracker + Calendar all reflect this meal. Silent-fail (local state
    // is already updated so the user sees their entry immediately).
    try {
      const isBarcode = String(picked.id || "").startsWith("barcode-");
      await axios.post(`${API}/daily-log/food`, {
        item: picked.name,
        qty: s,
        unit: picked.serving || "serving",
        kcal: Math.round((picked.cal || 0) * s),
        protein_g: Math.round((picked.p || 0) * s),
        carbs_g: Math.round((picked.c || 0) * s),
        fat_g: Math.round((picked.f || 0) * s),
        source_hub: isBarcode ? "barcode_scan" : "food_log_ui",
        date_pt: selectedDate,
      });
    } catch (e) {
      // Non-fatal — the entry is still visible locally.
      console.warn("[FoodLog] daily-log persist failed:", e?.message);
    }
  };

  const removeEntry = (id) => setLog((l) => l.filter((e) => e.id !== id));
  // Re-assign an entry to a different meal slot (Breakfast/Lunch/Dinner/Snack).
  // Useful when the user logs to the wrong column by accident.
  const moveEntry = (id, newMeal) => {
    if (!MEALS.includes(newMeal)) return;
    setLog((l) => l.map((e) => (e.id === id ? { ...e, meal: newMeal } : e)));
  };
  const clearToday = () => {
    if (!window.confirm("Clear all of today's logged food?")) return;
    setLog((l) => l.filter((e) => e.date !== todayKey()));
  };

  const groupedByMeal = useMemo(() => {
    const g = {};
    MEALS.forEach((m) => (g[m] = []));
    todayLog.forEach((e) => {
      if (!g[e.meal]) g[e.meal] = [];
      g[e.meal].push(e);
    });
    return g;
  }, [todayLog]);

  return (
    <section
      id="food"
      data-testid="food-section"
      className="py-24 md:py-36 bg-gradient-to-b from-[#0A0A0A] via-[#1a0d2b] to-[#0A0A0A] relative"
    >
      <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-[#4C1D95] opacity-15 blur-[150px] rounded-full pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="text-[#D4AF37] font-accent text-xs uppercase tracking-[0.3em] mb-3">
            Nutrition Tracker
          </div>
          <h2 className="font-display text-5xl md:text-7xl text-white leading-[0.9] mb-4">
            Fuel <span className="text-[#D4AF37]">The Work.</span>
          </h2>
          <p className="text-neutral-400 max-w-2xl mx-auto">
            Search a 100+ food library, log your meals, and watch your daily
            macros + key micros track in real time. Targets auto-pull from your
            TDEE — or set your own.
          </p>
        </div>

        {/* 📅 Shared calendar — retroactive + future day logging.
            Cross-connects Food + Gym + Cardio dots in one view. */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={() => setShowCalendar((v) => !v)}
              className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-neutral-300 hover:text-[#F1C84B] px-3 py-2 border border-zinc-800 hover:border-[#F1C84B] transition-colors"
              data-testid="food-calendar-toggle"
            >
              📅 {showCalendar ? "Hide Calendar" : "Calendar"}
              {!isViewingToday && (
                <span className="text-[#F1C84B] normal-case tracking-normal font-normal">
                  · {selectedDate}
                </span>
              )}
            </button>
            {!isViewingToday && (
              <button
                onClick={() => setSelectedDate(today)}
                className="text-[10px] uppercase tracking-widest font-mono text-neutral-400 hover:text-[#F1C84B]"
                data-testid="food-calendar-jump-today"
              >
                ← Back to Today
              </button>
            )}
          </div>
          {showCalendar && (
            <LogCalendar
              selectedDate={selectedDate}
              onSelectDate={(d) => setSelectedDate(d)}
              dotsByDate={dotsByDate}
              streams={["food", "gym", "cardio"]}
            />
          )}
        </div>

        {/* 🐦‍🔥 Phoenix Cauldron — "what can I cook with these?" AI recipe synth */}
        <div className="mb-8">
          <PhoenixCauldron />
        </div>

        {/* 🥗 Nutrient breakdown — macro donut + micros + daily bars */}
        <div className="mb-8">
          <NutrientChart />
        </div>

        {/* Targets summary bar */}
        <div className="bg-[#0F0F0F] border border-zinc-800 p-5 mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-6">
            <MacroProgress
              label="Calories"
              value={totals.cal}
              target={targets?.cal}
              unit="kcal"
              color="#D4AF37"
              testId="total-cal"
            />
            <MacroProgress
              label="Protein"
              value={totals.p}
              target={targets?.p}
              unit="g"
              color="#DC2626"
              testId="total-p"
            />
            <MacroProgress
              label="Carbs"
              value={totals.c}
              target={targets?.c}
              unit="g"
              color="#16A34A"
              testId="total-c"
            />
            <MacroProgress
              label="Fat"
              value={totals.f}
              target={targets?.f}
              unit="g"
              color="#3B82F6"
              testId="total-f"
            />
          </div>
          <button
            data-testid="food-targets-btn"
            onClick={() => setShowTargets(true)}
            className="text-xs uppercase tracking-widest font-bold text-neutral-400 hover:text-[#D4AF37] flex items-center gap-2 px-3 py-2 border border-zinc-800 hover:border-[#D4AF37] transition-colors"
          >
            <Settings size={14} />
            {manualTargets ? "Manual Targets" : tdeeData ? "Auto Targets" : "Set Targets"}
          </button>
        </div>

        {/* Micros bar */}
        <div className="bg-[#0F0F0F] border border-zinc-800 p-4 mb-6 flex flex-wrap gap-6 text-sm">
          <span className="text-neutral-400">
            Fiber: <span className="text-white font-bold" data-testid="total-fib">{fmt1(totals.fib)} g</span>
          </span>
          <span className="text-neutral-400">
            Sugar: <span className="text-white font-bold" data-testid="total-sug">{fmt1(totals.sug)} g</span>
          </span>
          <span className="text-neutral-400">
            Sodium: <span className="text-white font-bold" data-testid="total-na">{fmt(totals.na)} mg</span>
          </span>
          {quickCalToday > 0 && (
            <span
              data-testid="quick-extras-line"
              className="text-[#D4AF37] ml-auto"
              title={`${quickEntries.length} Quick Log entry${quickEntries.length === 1 ? "" : "ies"} from Today's Extras`}
            >
              + Today's Extras: <span className="font-bold tabular-nums">{fmt(quickCalToday)} kcal</span>
            </span>
          )}
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Food browser */}
          <div className="lg:col-span-7 bg-[#0F0F0F] border border-zinc-800 p-6">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500"
                />
                <input
                  data-testid="food-search"
                  className="input-omni pl-10"
                  placeholder="Search foods…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <button
                data-testid="food-scan-btn"
                onClick={() => {
                  if (isPremium) setShowScanner(true);
                  else setShowPremiumGate(true);
                }}
                className={`btn-omni text-xs px-4 py-3 ${
                  isPremium ? "btn-red" : "btn-outline-gold"
                }`}
                title={
                  isPremium
                    ? "Scan a barcode"
                    : "Premium feature — tap to unlock"
                }
              >
                {isPremium ? <ScanLine size={14} /> : <Lock size={12} />}
                Scan
                {!isPremium && (
                  <span className="ml-1 text-[10px] uppercase tracking-widest text-[#D4AF37]">
                    Pro
                  </span>
                )}
              </button>
              <FoodPhotoButton
                variant="ghost"
                label="📷 Snap"
                quickLogType="other"
              />
              <button
                data-testid="food-quick-add-btn"
                onClick={() => setShowQuickAdd(true)}
                className="btn-omni btn-purple text-xs px-4 py-3"
                title="Quick log without saving"
              >
                <Zap size={14} /> Quick Add
              </button>
              <button
                data-testid="food-add-custom-btn"
                onClick={() => setShowCustom(true)}
                className="btn-omni btn-outline-gold text-xs px-4 py-3"
              >
                <Plus size={14} /> Custom
              </button>
            </div>

            {/* Category chips */}
            <div className="flex flex-wrap gap-2 mb-4">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  data-testid={`food-cat-${c}`}
                  onClick={() => setCategory(c)}
                  className={`px-3 py-1 text-xs font-bold uppercase tracking-widest border transition-colors ${
                    category === c
                      ? "border-[#D4AF37] bg-[#D4AF37] text-black"
                      : "border-zinc-800 text-neutral-400 hover:border-zinc-600"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>

            {/* Food list */}
            <div className="border border-zinc-800 max-h-[500px] overflow-y-auto">
              {filtered.length === 0 ? (
                <div className="p-10 text-center text-neutral-500 text-sm">
                  No foods match. Try clearing filters or add a custom food.
                </div>
              ) : (
                filtered.map((food) => (
                  <button
                    key={food.id}
                    data-testid={`food-item-${food.id}`}
                    onClick={() => setPicked(food)}
                    className={`w-full text-left p-3 border-b border-zinc-900 hover:bg-[#1A1A1A] transition-colors flex items-center gap-3 ${
                      picked?.id === food.id ? "bg-[#D4AF37]/10" : ""
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-base text-white truncate">
                        {food.name}
                        {food.custom && (
                          <span className="ml-2 text-[10px] bg-[#4C1D95] text-white px-1.5 py-0.5 uppercase tracking-widest">
                            Custom
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-neutral-500">
                        {food.category} · {food.serving}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="font-display text-lg text-[#D4AF37]">
                        {fmt(food.cal)}
                      </div>
                      <div className="text-[10px] text-neutral-500">
                        P{fmt(food.p)} · C{fmt(food.c)} · F{fmt(food.f)}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Add panel + Today's log */}
          <div className="lg:col-span-5 space-y-6">
            {/* Add modal-inline */}
            {picked && (
              <div
                data-testid="food-add-panel"
                className="bg-[#0F0F0F] border-2 border-[#D4AF37] p-6"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
                      Add To Log
                    </div>
                    <h3 className="font-display text-2xl text-white leading-tight">
                      {picked.name}
                    </h3>
                    <div className="text-xs text-neutral-500 mt-1">
                      Per {picked.serving}: {picked.cal} kcal · P{picked.p} · C{picked.c} · F{picked.f}
                    </div>
                  </div>
                  <button
                    data-testid="food-add-close"
                    onClick={() => setPicked(null)}
                    className="text-neutral-500 hover:text-white"
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-1">
                      Servings
                    </label>
                    <input
                      data-testid="food-servings"
                      type="text"
                      inputMode="decimal"
                      className="input-omni"
                      value={servings}
                      onChange={(e) =>
                        setServings(e.target.value.replace(/[^0-9.]/g, ""))
                      }
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-1">
                      Meal
                    </label>
                    <select
                      data-testid="food-meal"
                      className="input-omni"
                      value={meal}
                      onChange={(e) => setMeal(e.target.value)}
                    >
                      {MEALS.map((m) => (
                        <option key={m}>{m}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Live preview */}
                <div className="bg-[#0A0A0A] border border-zinc-800 p-3 mb-4 grid grid-cols-4 gap-2 text-center">
                  {[
                    { l: "kcal", v: fmt((picked.cal || 0) * (parseFloat(servings) || 0)) },
                    { l: "P g", v: fmt1((picked.p || 0) * (parseFloat(servings) || 0)) },
                    { l: "C g", v: fmt1((picked.c || 0) * (parseFloat(servings) || 0)) },
                    { l: "F g", v: fmt1((picked.f || 0) * (parseFloat(servings) || 0)) },
                  ].map((x) => (
                    <div key={x.l}>
                      <div className="font-display text-xl text-white">{x.v}</div>
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                        {x.l}
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  data-testid="food-add-submit"
                  onClick={addFood}
                  className="btn-omni btn-red w-full"
                >
                  <Plus size={16} /> Log Food
                </button>
              </div>
            )}

            {/* Today's log grouped by meal */}
            <div className="bg-[#0F0F0F] border border-zinc-800 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
                    Today's Intake
                  </div>
                  <div className="font-display text-2xl text-white">
                    {todayLog.length} {todayLog.length === 1 ? "Item" : "Items"}
                  </div>
                </div>
                <button
                  data-testid="food-clear-today"
                  onClick={clearToday}
                  disabled={todayLog.length === 0}
                  className="text-xs uppercase tracking-widest font-bold text-neutral-400 hover:text-[#DC2626] disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1.5"
                >
                  <Trash2 size={12} /> Clear
                </button>
              </div>

              {todayLog.length === 0 ? (
                <p className="text-neutral-500 text-sm py-8 text-center">
                  Nothing logged yet. Pick a food to start.
                </p>
              ) : (
                <div className="space-y-4 max-h-[400px] overflow-y-auto pr-1">
                  {MEALS.map((m) => {
                    const items = groupedByMeal[m] || [];
                    if (items.length === 0) return null;
                    return (
                      <div key={m}>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-2">
                          {m}
                        </div>
                        <div className="space-y-1.5">
                          {items.map((e) => (
                            <div
                              key={e.id}
                              data-testid={`food-log-entry-${e.id}`}
                              className="bg-[#0A0A0A] border border-zinc-800 p-2.5 group flex items-start justify-between gap-2"
                            >
                              <div className="flex-1 min-w-0">
                                <div className="font-display text-sm text-white truncate">
                                  {e.name}
                                </div>
                                <div className="text-[10px] text-neutral-500">
                                  {e.servings}× · P{fmt1(e.p)} · C{fmt1(e.c)} · F{fmt1(e.f)}
                                </div>
                              </div>
                              <div className="text-right flex-shrink-0">
                                <div className="font-display text-base text-[#D4AF37]">
                                  {fmt(e.cal)}
                                </div>
                                <div className="flex items-center gap-1 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                                  {/* Move-to-meal: native select keeps it mobile-friendly */}
                                  <div
                                    className="relative inline-flex items-center"
                                    title="Move to another meal"
                                  >
                                    <ArrowRightLeft size={11} className="text-neutral-600" />
                                    <select
                                      data-testid={`food-move-${e.id}`}
                                      value={e.meal}
                                      onChange={(ev) => moveEntry(e.id, ev.target.value)}
                                      onClick={(ev) => ev.stopPropagation()}
                                      aria-label="Move to meal"
                                      className="appearance-none bg-transparent text-[10px] uppercase tracking-widest text-neutral-500 hover:text-[#D4AF37] cursor-pointer pl-1 pr-0 py-0 border-0 focus:outline-none focus:ring-0"
                                    >
                                      {MEALS.map((m) => (
                                        <option key={m} value={m} className="bg-[#0A0A0A] text-white">
                                          {m}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <button
                                    data-testid={`food-remove-${e.id}`}
                                    onClick={() => removeEntry(e.id)}
                                    className="text-neutral-600 hover:text-[#DC2626] transition-colors"
                                    aria-label="Remove"
                                  >
                                    <X size={12} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Custom food modal */}
      {showCustom && (
        <CustomFoodModal
          onClose={() => setShowCustom(false)}
          onSave={(food) => {
            setCustomFoods((c) => [...c, food]);
            setShowCustom(false);
          }}
        />
      )}

      {/* Quick Add modal — log a one-off without saving to library */}
      {showQuickAdd && (
        <QuickAddModal
          onClose={() => setShowQuickAdd(false)}
          onLog={(entry) => {
            setLog((l) => [...l, entry]);
            setShowQuickAdd(false);
            logActivity?.("food");
          }}
        />
      )}

      {/* Barcode scanner */}
      {showScanner && (
        <BarcodeScanner
          onClose={() => setShowScanner(false)}
          onProductFound={(product) => {
            // Open the add panel pre-populated with the scanned product
            setPicked({
              id: `barcode-${product.barcode}`,
              name: product.brand
                ? `${product.brand} — ${product.name}`
                : product.name,
              category: "Restaurant",
              serving: product.serving || "100g",
              cal: product.cal,
              p: product.p,
              c: product.c,
              f: product.f,
              fib: product.fib,
              sug: product.sug,
              na: product.na,
            });
            setShowScanner(false);
          }}
        />
      )}

      {/* Premium gate */}
      <PremiumGate
        open={showPremiumGate}
        onClose={() => setShowPremiumGate(false)}
        feature="Barcode scanner"
      />

      {/* Targets modal */}
      {showTargets && (
        <TargetsModal
          onClose={() => setShowTargets(false)}
          tdeeData={tdeeData}
          current={targets}
          manualSet={!!manualTargets}
          onClear={() => {
            setManualTargets(null);
            setShowTargets(false);
          }}
          onSave={(t) => {
            setManualTargets(t);
            setShowTargets(false);
          }}
        />
      )}
    </section>
  );
}

/* -------- Macro progress bar -------- */
function MacroProgress({ label, value, target, unit, color, testId }) {
  const pct = target ? Math.min(100, (value / target) * 100) : 0;
  const over = target && value > target;
  return (
    <div className="min-w-[140px]">
      <div className="flex items-end justify-between mb-1">
        <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 font-bold">
          {label}
        </div>
        <div className="text-[10px] text-neutral-500">
          {target ? `${fmt(target)} ${unit}` : "—"}
        </div>
      </div>
      <div data-testid={testId} className="font-display text-2xl text-white">
        {fmt(value)}{" "}
        <span className="text-xs text-neutral-500 font-accent font-normal">{unit}</span>
      </div>
      <div className="h-1.5 bg-[#1F1F1F] mt-1.5 relative overflow-hidden">
        <div
          className="h-full transition-all duration-500"
          style={{
            width: `${pct}%`,
            background: over ? "#DC2626" : color,
          }}
        />
      </div>
    </div>
  );
}

/* -------- Custom food modal -------- */
function CustomFoodModal({ onClose, onSave }) {
  const [form, setForm] = useState({
    name: "",
    category: "Protein",
    serving: "100g",
    cal: "",
    p: "",
    c: "",
    f: "",
    fib: "",
    sug: "",
    na: "",
  });
  const upd = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const save = () => {
    if (!form.name.trim() || !form.cal) return;
    onSave({
      id: `custom-${Date.now()}`,
      name: form.name.trim(),
      category: form.category,
      serving: form.serving || "1 serving",
      cal: parseFloat(form.cal) || 0,
      p: parseFloat(form.p) || 0,
      c: parseFloat(form.c) || 0,
      f: parseFloat(form.f) || 0,
      fib: parseFloat(form.fib) || 0,
      sug: parseFloat(form.sug) || 0,
      na: parseFloat(form.na) || 0,
      custom: true,
    });
  };

  return (
    <div
      data-testid="custom-food-modal"
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-[#0F0F0F] border-2 border-[#D4AF37] max-w-lg w-full p-6 my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
              Add Your Own
            </div>
            <h3 className="font-display text-3xl text-white">Custom Food</h3>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-white">
            <X size={22} />
          </button>
        </div>

        <div className="space-y-3">
          <Field label="Food Name *" testId="cf-name">
            <input
              data-testid="cf-name"
              className="input-omni"
              value={form.name}
              onChange={upd("name")}
              placeholder="e.g., Mom's Meatloaf"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <select
                data-testid="cf-category"
                className="input-omni"
                value={form.category}
                onChange={upd("category")}
              >
                {CATEGORIES.filter((c) => c !== "All").map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </Field>
            <Field label="Serving Size">
              <input
                data-testid="cf-serving"
                className="input-omni"
                value={form.serving}
                onChange={upd("serving")}
                placeholder="e.g., 1 cup, 100g"
              />
            </Field>
          </div>

          <div className="grid grid-cols-4 gap-2">
            <NumField label="kcal *" v={form.cal} onChange={upd("cal")} testId="cf-cal" />
            <NumField label="Protein g" v={form.p} onChange={upd("p")} testId="cf-p" />
            <NumField label="Carbs g" v={form.c} onChange={upd("c")} testId="cf-c" />
            <NumField label="Fat g" v={form.f} onChange={upd("f")} testId="cf-f" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <NumField label="Fiber g" v={form.fib} onChange={upd("fib")} testId="cf-fib" />
            <NumField label="Sugar g" v={form.sug} onChange={upd("sug")} testId="cf-sug" />
            <NumField label="Sodium mg" v={form.na} onChange={upd("na")} testId="cf-na" />
          </div>
        </div>

        <button
          data-testid="cf-save"
          onClick={save}
          disabled={!form.name.trim() || !form.cal}
          className="btn-omni btn-red w-full mt-5 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save size={16} /> Save To Library
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-bold block mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
function NumField({ label, v, onChange, testId }) {
  return (
    <Field label={label}>
      <input
        data-testid={testId}
        type="text"
        inputMode="decimal"
        className="input-omni"
        value={v}
        onChange={onChange}
        placeholder="0"
      />
    </Field>
  );
}

/* -------- Quick Add modal — log one-off, no library save -------- */
function QuickAddModal({ onClose, onLog }) {
  const [form, setForm] = useState({
    name: "",
    meal: "Breakfast",
    cal: "",
    p: "",
    c: "",
    f: "",
    fib: "",
    sug: "",
    na: "",
  });
  const upd = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const log = () => {
    if (!form.name.trim() || !form.cal) return;
    onLog({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      date: todayKey(),
      meal: form.meal,
      foodId: `quick-${Date.now()}`,
      name: form.name.trim(),
      serving: "1 entry",
      servings: 1,
      cal: parseFloat(form.cal) || 0,
      p: parseFloat(form.p) || 0,
      c: parseFloat(form.c) || 0,
      f: parseFloat(form.f) || 0,
      fib: parseFloat(form.fib) || 0,
      sug: parseFloat(form.sug) || 0,
      na: parseFloat(form.na) || 0,
    });
  };

  return (
    <div
      data-testid="quick-add-modal"
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-[#0F0F0F] border-2 border-[#4C1D95] max-w-lg w-full p-6 my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#4C1D95] font-bold">
              One-Off Entry
            </div>
            <h3 className="font-display text-3xl text-white">Quick Add</h3>
            <p className="text-xs text-neutral-500 mt-1">
              Logs to today only — won't save to your food library.
            </p>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-white">
            <X size={22} />
          </button>
        </div>

        <div className="space-y-3">
          <Field label="What did you eat? *">
            <input
              data-testid="qa-name"
              className="input-omni"
              value={form.name}
              onChange={upd("name")}
              placeholder="e.g., Restaurant burrito bowl"
            />
          </Field>
          <Field label="Meal">
            <select
              data-testid="qa-meal"
              className="input-omni"
              value={form.meal}
              onChange={upd("meal")}
            >
              {MEALS.map((m) => (
                <option key={m}>{m}</option>
              ))}
            </select>
          </Field>

          <div className="grid grid-cols-4 gap-2">
            <NumField label="kcal *" v={form.cal} onChange={upd("cal")} testId="qa-cal" />
            <NumField label="Protein g" v={form.p} onChange={upd("p")} testId="qa-p" />
            <NumField label="Carbs g" v={form.c} onChange={upd("c")} testId="qa-c" />
            <NumField label="Fat g" v={form.f} onChange={upd("f")} testId="qa-f" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <NumField label="Fiber g" v={form.fib} onChange={upd("fib")} testId="qa-fib" />
            <NumField label="Sugar g" v={form.sug} onChange={upd("sug")} testId="qa-sug" />
            <NumField label="Sodium mg" v={form.na} onChange={upd("na")} testId="qa-na" />
          </div>
        </div>

        <button
          data-testid="qa-log"
          onClick={log}
          disabled={!form.name.trim() || !form.cal}
          className="btn-omni btn-purple w-full mt-5 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Zap size={16} /> Log Entry
        </button>
      </div>
    </div>
  );
}

/* -------- Targets modal -------- */
function TargetsModal({ onClose, tdeeData, current, manualSet, onClear, onSave }) {
  const [form, setForm] = useState({
    cal: current?.cal || "",
    p: current?.p || "",
    c: current?.c || "",
    f: current?.f || "",
  });
  const upd = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const save = () =>
    onSave({
      cal: parseFloat(form.cal) || 0,
      p: parseFloat(form.p) || 0,
      c: parseFloat(form.c) || 0,
      f: parseFloat(form.f) || 0,
    });

  return (
    <div
      data-testid="targets-modal"
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-[#0F0F0F] border-2 border-[#D4AF37] max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
              Daily Targets
            </div>
            <h3 className="font-display text-3xl text-white">
              {manualSet ? "Manual Override" : "Override Auto"}
            </h3>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-white">
            <X size={22} />
          </button>
        </div>

        {tdeeData ? (
          <div className="text-xs text-neutral-400 mb-4 p-3 bg-[#0A0A0A] border border-zinc-800">
            Auto target from Tools section:{" "}
            <span className="text-[#D4AF37] font-bold">
              {Math.round(tdeeData.target)} kcal/day
            </span>
            . Default split 30P / 45C / 25F.
          </div>
        ) : (
          <div className="text-xs text-neutral-400 mb-4 p-3 bg-[#0A0A0A] border border-zinc-800">
            Tip: run the Daily Calorie calculator in <strong>Tools</strong> first to
            auto-fill these — or set your own below.
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Calories">
            <input
              data-testid="tgt-cal"
              type="text"
              inputMode="decimal"
              className="input-omni"
              value={form.cal}
              onChange={upd("cal")}
              placeholder="2800"
            />
          </Field>
          <Field label="Protein g">
            <input
              data-testid="tgt-p"
              type="text"
              inputMode="decimal"
              className="input-omni"
              value={form.p}
              onChange={upd("p")}
              placeholder="210"
            />
          </Field>
          <Field label="Carbs g">
            <input
              data-testid="tgt-c"
              type="text"
              inputMode="decimal"
              className="input-omni"
              value={form.c}
              onChange={upd("c")}
              placeholder="315"
            />
          </Field>
          <Field label="Fat g">
            <input
              data-testid="tgt-f"
              type="text"
              inputMode="decimal"
              className="input-omni"
              value={form.f}
              onChange={upd("f")}
              placeholder="78"
            />
          </Field>
        </div>

        <div className="flex gap-3 mt-5">
          <button
            data-testid="tgt-save"
            onClick={save}
            className="btn-omni btn-red flex-1"
          >
            <Save size={16} /> Save Manual
          </button>
          {manualSet && (
            <button
              data-testid="tgt-clear"
              onClick={onClear}
              className="btn-omni btn-outline-gold"
            >
              Use Auto
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

